import { create } from 'zustand';

import { getView, type EngineView } from '../engine/prism.js';

/**
 * Scene view settings (per user, kept in localStorage): snapping, pivot mode,
 * split layout, view mode, camera clipping, the selected-camera preview and
 * which viewport overlay panels are open. Every change is pushed to the live
 * engine view; `editorView` events from the engine (view axes, active view)
 * come back through {@link SceneViewState.fromEngine}.
 */

export type GridPlane = 'xz' | 'xy' | 'yz';
export type PivotMode = 'pivot' | 'center';
export type ViewLayout = 'single' | 'two' | 'four';
export type ViewAxis = 'top' | 'bottom' | 'front' | 'back' | 'right' | 'left';

/** prism_runtime `SnapSettings` (camelCase JSON). */
export interface SnapSettings {
  translate: number;
  rotate: number;
  scale: number;
  absolute: boolean;
  grid: boolean;
  gridSize: number;
  gridPlane: GridPlane;
  vertex: boolean;
  surface: boolean;
  alignToSurface: boolean;
  /** Which tools snap (Unity's move / angle / scale snap toggles; Blender's Affect). */
  affectTranslate: boolean;
  affectRotate: boolean;
  affectScale: boolean;
}

export const DEFAULT_SNAP: SnapSettings = {
  translate: 0.5,
  rotate: 15,
  scale: 0.1,
  absolute: true,
  grid: true,
  gridSize: 1,
  gridPlane: 'xz',
  vertex: false,
  surface: false,
  alignToSurface: false,
  affectTranslate: true,
  affectRotate: true,
  affectScale: true,
};

/** View modes: shading modes, then renderer debug outputs (`prism_render::DebugView`). */
export const VIEW_MODES: { id: string; label: string; group: string }[] = [
  { id: 'shaded', label: 'Shaded', group: 'Shading Mode' },
  { id: 'wireframe', label: 'Wireframe', group: 'Shading Mode' },
  { id: 'shadedWireframe', label: 'Shaded Wireframe', group: 'Shading Mode' },
  { id: 'unlit', label: 'Unlit', group: 'Shading Mode' },
  { id: 'albedo', label: 'Albedo', group: 'Material' },
  { id: 'normals', label: 'Normals', group: 'Material' },
  { id: 'roughness', label: 'Roughness', group: 'Material' },
  { id: 'metallic', label: 'Metallic', group: 'Material' },
  { id: 'validate', label: 'Validate Albedo (PBR)', group: 'Material' },
  { id: 'lightingOnly', label: 'Lighting Only', group: 'Lighting' },
  { id: 'shadowCascades', label: 'Shadow Cascades', group: 'Lighting' },
  { id: 'overdraw', label: 'Overdraw', group: 'Miscellaneous' },
];

export type OverlayId = 'tools' | 'snapping' | 'viewOptions' | 'orientation' | 'cameraPreview';

/** Open / collapsed / hidden state per overlay panel. */
export type OverlayState = Record<OverlayId, 'open' | 'collapsed' | 'hidden'>;

export const OVERLAY_PRESETS: Record<string, OverlayState> = {
  Default: { tools: 'open', snapping: 'collapsed', viewOptions: 'collapsed', orientation: 'open', cameraPreview: 'open' },
  Minimal: { tools: 'collapsed', snapping: 'hidden', viewOptions: 'hidden', orientation: 'open', cameraPreview: 'collapsed' },
  'All open': { tools: 'open', snapping: 'open', viewOptions: 'open', orientation: 'open', cameraPreview: 'open' },
  Hidden: { tools: 'hidden', snapping: 'hidden', viewOptions: 'hidden', orientation: 'hidden', cameraPreview: 'hidden' },
};

export interface EditorViewInfo {
  axis: ViewAxis | null;
  ortho: boolean;
  yaw: number;
  pitch: number;
  /** `[x, y, w, h]` fractions, origin bottom-left. */
  rect: [number, number, number, number];
}

export interface SceneViewSettings {
  snapSettings: SnapSettings;
  pivot: PivotMode;
  layout: ViewLayout;
  viewMode: string;
  cameraPreview: boolean;
  /** Camera entity the preview is pinned to (`''` = the selected camera). */
  previewCamera: string;
  near: number;
  far: number;
  fov: number;
  overlays: OverlayState;
  overlayPreset: string;
}

export interface SceneViewState extends SceneViewSettings {
  /** Live state reported by the engine. */
  views: EditorViewInfo[];
  active: number;
  preview: { id: string; rect: [number, number, number, number] } | null;

  setSnapSettings: (patch: Partial<SnapSettings>) => void;
  setPivot: (pivot: PivotMode) => void;
  setLayout: (layout: ViewLayout) => void;
  setViewMode: (mode: string) => void;
  setCameraPreview: (on: boolean) => void;
  setPreviewCamera: (id: string) => void;
  /** Moves the selection to the closest grid points (one undo step). */
  snapSelectionToGrid: () => void;
  setClip: (patch: Partial<Pick<SceneViewSettings, 'near' | 'far' | 'fov'>>) => void;
  /** Points the active view: an axis, `persp`, `ortho`, `toggle` or `camera`. */
  setView: (view: ViewAxis | 'persp' | 'ortho' | 'toggle' | 'camera') => void;
  setOverlay: (id: OverlayId, state: OverlayState[OverlayId]) => void;
  applyPreset: (name: string) => void;
  /** Sends every setting to a (new) engine view. */
  pushAll: (view: EngineView) => void;
  fromEngine: (event: Record<string, unknown>) => void;
}

const STORAGE_KEY = 'prism.sceneView';

export const DEFAULT_VIEW_SETTINGS: SceneViewSettings = {
  snapSettings: DEFAULT_SNAP,
  pivot: 'pivot',
  layout: 'single',
  viewMode: 'shaded',
  cameraPreview: true,
  previewCamera: '',
  // Unity's SceneView.CameraSettings defaults (dynamic clipping aside).
  near: 0.03,
  far: 10000,
  fov: 60,
  overlays: OVERLAY_PRESETS.Default!,
  overlayPreset: 'Default',
};

/** Stored settings over the defaults; anything malformed falls back. */
export function parseViewSettings(raw: string | null): SceneViewSettings {
  if (!raw) return DEFAULT_VIEW_SETTINGS;
  try {
    const v = JSON.parse(raw) as Partial<SceneViewSettings>;
    if (!v || typeof v !== 'object') return DEFAULT_VIEW_SETTINGS;
    return {
      ...DEFAULT_VIEW_SETTINGS,
      ...v,
      snapSettings: { ...DEFAULT_SNAP, ...(v.snapSettings ?? {}) },
      overlays: { ...DEFAULT_VIEW_SETTINGS.overlays, ...(v.overlays ?? {}) },
    };
  } catch {
    return DEFAULT_VIEW_SETTINGS;
  }
}

function load(): SceneViewSettings {
  try {
    return parseViewSettings(typeof localStorage === 'undefined' ? null : localStorage.getItem(STORAGE_KEY));
  } catch {
    return DEFAULT_VIEW_SETTINGS;
  }
}

function save(s: SceneViewSettings): void {
  const { snapSettings, pivot, layout, viewMode, cameraPreview, previewCamera, near, far, fov, overlays, overlayPreset } = s;
  try {
    if (typeof localStorage !== 'undefined')
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ snapSettings, pivot, layout, viewMode, cameraPreview, previewCamera, near, far, fov, overlays, overlayPreset }),
      );
  } catch {
    // Storage blocked: settings last for this session.
  }
}

const engine = () => getView('scene');

export const useSceneView = create<SceneViewState>((set, get) => {
  const update = (patch: Partial<SceneViewSettings>) => {
    set(patch);
    save(get());
  };
  return {
    ...load(),
    views: [],
    active: 0,
    preview: null,

    setSnapSettings: (patch) => {
      update({ snapSettings: { ...get().snapSettings, ...patch } });
      engine()?.setOption('snapSettings', JSON.stringify(patch));
    },
    setPivot: (pivot) => {
      update({ pivot });
      engine()?.setOption('pivot', pivot);
    },
    setLayout: (layout) => {
      update({ layout });
      engine()?.setOption('viewLayout', layout);
    },
    setViewMode: (viewMode) => {
      update({ viewMode });
      engine()?.setOption('viewMode', viewMode);
    },
    setCameraPreview: (cameraPreview) => {
      update({ cameraPreview });
      engine()?.setOption('cameraPreview', String(cameraPreview));
    },
    setPreviewCamera: (previewCamera) => {
      update({ previewCamera });
      engine()?.setOption('previewCamera', previewCamera);
    },
    snapSelectionToGrid: () => engine()?.setOption('snapSelectionToGrid', ''),
    setClip: (patch) => {
      update(patch);
      const { near, far, fov } = get();
      engine()?.setOption('cameraClip', JSON.stringify({ near, far, fov }));
    },
    setView: (view) => engine()?.setOption('view', view),
    setOverlay: (id, state) => update({ overlays: { ...get().overlays, [id]: state }, overlayPreset: '' }),
    applyPreset: (name) => {
      const preset = OVERLAY_PRESETS[name];
      if (preset) update({ overlays: preset, overlayPreset: name });
    },
    pushAll: (view) => {
      const s = get();
      view.setOption('hostEditKeys', 'true');
      view.setOption('snapSettings', JSON.stringify(s.snapSettings));
      view.setOption('pivot', s.pivot);
      view.setOption('viewLayout', s.layout);
      view.setOption('viewMode', s.viewMode);
      view.setOption('cameraPreview', String(s.cameraPreview));
      view.setOption('previewCamera', s.previewCamera);
      view.setOption('cameraClip', JSON.stringify({ near: s.near, far: s.far, fov: s.fov }));
    },
    fromEngine: (event) => {
      const views = Array.isArray(event.views) ? (event.views as EditorViewInfo[]) : get().views;
      const preview = (event.preview ?? null) as SceneViewState['preview'];
      const patch: Partial<SceneViewState> = { views, active: Number(event.active ?? 0), preview };
      // Keys (Z pivot) change these in the engine too.
      if (event.pivot === 'pivot' || event.pivot === 'center') patch.pivot = event.pivot;
      set(patch);
      if (patch.pivot && patch.pivot !== load().pivot) save(get());
    },
  };
});
